import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CountSitesApplicationErrors,
  CountSitesOptions,
  CountSitesResponse,
  QuerySitesApplicationErrors,
  QuerySitesResponse,
  SiteCreatedEnvelope,
  SiteDeletedEnvelope,
  SiteQuery,
  SiteUpdatedEnvelope,
  SitesQueryBuilder,
  countSites as universalCountSites,
  querySites as universalQuerySites,
  typedQuerySites as universalTypedQuerySites,
} from './site-list-v2-site-sites.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/sites' };

export function querySites(httpClient: HttpClient): QuerySitesSignature {
  return () =>
    universalQuerySites(
      // @ts-ignore
      { httpClient }
    );
}

interface QuerySitesSignature {
  /**
   * Retrieves a list of up to 100 sites per request, given the provided paging, sorting and filtering.
   *
   * > **Important**: You can only call this method when authenticated as a Wix user or by using an account level API key.
   */
  (): SitesQueryBuilder;
}

export function typedQuerySites(
  httpClient: HttpClient
): TypedQuerySitesSignature {
  return (query: SiteQuery) =>
    universalTypedQuerySites(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQuerySitesSignature {
  /** */
  (query: SiteQuery): Promise<
    NonNullablePaths<
      QuerySitesResponse,
      | `sites`
      | `sites.${number}._id`
      | `sites.${number}.name`
      | `sites.${number}.displayName`
      | `sites.${number}.published`
      | `sites.${number}.premium`
      | `sites.${number}.viewUrl`
      | `sites.${number}.editUrl`
      | `sites.${number}.thumbnail`
      | `sites.${number}.ownerAccountId`
      | `sites.${number}.editorType`
      | `sites.${number}.blocked`
      | `sites.${number}.namespace`
      | `sites.${number}.domainConnected`,
      4
    > & {
      __applicationErrorsType?: QuerySitesApplicationErrors;
    }
  >;
}

export function countSites(httpClient: HttpClient): CountSitesSignature {
  return (options?: CountSitesOptions) =>
    universalCountSites(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountSitesSignature {
  /**
   * Retrieves the number of sites, given the provided filtering.
   * See [Query Sites](https://dev.wix.com/docs/rest/account-level-apis/sites/sites/query-sites) for a list of supported filters.
   *
   * > **Important**: You can only call this method when authenticated as a Wix user or by using an account level API key.
   */
  (options?: CountSitesOptions): Promise<
    NonNullablePaths<CountSitesResponse, `count`, 2> & {
      __applicationErrorsType?: CountSitesApplicationErrors;
    }
  >;
}

export const onSiteCreated = EventDefinition(
  'wix.site_list.v2.site_created',
  true,
  (event: SiteCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.trashedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<SiteCreatedEnvelope>();
export const onSiteDeleted = EventDefinition(
  'wix.site_list.v2.site_deleted',
  true,
  (event: SiteDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.trashedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<SiteDeletedEnvelope>();
export const onSiteUpdated = EventDefinition(
  'wix.site_list.v2.site_updated',
  true,
  (event: SiteUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.trashedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<SiteUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  CountSitesOptions,
  CountSitesRequest,
  CountSitesResponse,
  CursorPaging,
  Cursors,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorType,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  Namespace,
  PagingMetadataV2,
  QuerySitesRequest,
  QuerySitesResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RestoreInfo,
  Site,
  SiteCreatedEnvelope,
  SiteDeletedEnvelope,
  SiteQuerySpec,
  SiteUpdatedEnvelope,
  SitesQueryBuilder,
  SitesQueryResult,
  SortOrder,
  Sorting,
  WebhookIdentityType,
  utils,
} from './site-list-v2-site-sites.universal.js';
